import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { SideBarKey } from '../../../components/layout/HomePage/Sidebar/SidebarConstants';
import {
  AssetType,
  IAccountDetails,
  IAccountDetailsResponse,
  IAssessmentStateResponse,
  IAuditHistoryPayload,
  IAuditHistoryResponse,
  IProjectOwnersPayload,
  IProjectOwnersResponse,
} from '../../models/DashboardModels';
import DashboardService from '../../services/DashboardService/DashboardService';
import {
  DashboardAction,
  DashboardActions,
  DashboardActionTypes,
} from '../actions/DashboardActions';
import { OverviewActions } from '../actions/OverviewActions';
import { SidebarActions } from '../actions/SidebarActions';
import { DashboardHeaderConstants } from '../../../pages/Dashboard/DashboardHeader/DashboardHeaderConstants';

// Module-level freshness cache to dedupe the bootstrap fan-out.
// Several mount points (Dashboard, OverviewHeader, DashboardMobile) all
// dispatch GET_ALL_ACCOUNTS_START on first load. With takeLatest the
// in-flight calls cancel each other, but we also skip refetching when we
// have data that is less than ACCOUNTS_TTL_MS old — that turns the
// duplicate dispatches into a no-op instead of N network calls.
const ACCOUNTS_TTL_MS = 30_000;
let lastAccountsFetchAt = 0;

export function* getAllAccountsWorker(action: DashboardAction) {
  try {
    const existing: IAccountDetails[] = yield select(
      (s: { dashboard: { accountsProjectsMapping: IAccountDetails[] } }) =>
        s.dashboard.accountsProjectsMapping,
    );
    const fresh = Date.now() - lastAccountsFetchAt < ACCOUNTS_TTL_MS;
    if (existing && existing.length > 0 && fresh) {
      // Nothing to do — short-circuit the loading flag back to false so the
      // global loader doesn't spin forever waiting on a request we skipped.
      yield put(DashboardActions.getAllAccountsSuccess(
        // re-emit cached shape so the reducer leaves state intact
        existing as unknown as IAccountDetailsResponse[],
      ));
      return;
    }
    // API call to get all accounts
    const response: IAccountDetailsResponse[] = yield DashboardService.getUserProjects();
    lastAccountsFetchAt = Date.now();
    yield put(DashboardActions.getAllAccountsSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(DashboardActions.getAllAccountsError(error));
    }
  }
}

export function* getAuditHistoryWorker(action: DashboardAction) {
  try {
    const response: IAuditHistoryResponse = yield DashboardService.getAuditDetails();

    if (response && response.auditHistory && response.auditHistory.dashboard) {
      const {
        auditHistory: {
          dashboard: { account, project, enabledDiscoverAssessItems },
        },
      } = response;

      if (account)
        yield put(
          OverviewActions.selectAccount({
            accountId: account.id!,
            accountName: account.name!,
            assetType: account.assetType,
          }),
        );
      if (project)
        yield put(
          OverviewActions.selectProject({ projectId: project.id!, projectName: project.name! }),
        );
      if (enabledDiscoverAssessItems)
        yield put(
          DashboardActions.setDiscoverAssessList(
            enabledDiscoverAssessItems.map((item) => parseInt(item.toString())) || [],
          ),
        );
      if (!account && !project) {
        yield put(SidebarActions.setSidebarKey(SideBarKey.DASHBOARD));
      }
    }

    yield put(DashboardActions.getAuditHistorySuccess());
  } catch (error) {
    if (error instanceof Error) {
      yield put(DashboardActions.getAuditHistoryError(error));
    }
  }
}

export function* getProjectOwnersWorker(action: DashboardAction) {
  try {
    let response: IProjectOwnersResponse[];
    const { assetType } = action.data as IProjectOwnersPayload;
    if (assetType === AssetType.TANGRA) {
      response = yield DashboardService.getProjectOwners(action.data as IProjectOwnersPayload);
    } else {
      response = yield DashboardService.getProjectOwnersCWB(action.data as IProjectOwnersPayload);
    }
    yield put(DashboardActions.getProjectOwnersSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(DashboardActions.getProjectOwnersError(error));
    }
  }
}

export function* saveAuditHistoryWorker(action: DashboardAction) {
  try {
    const { data } = action;

    yield DashboardService.saveAuditDetails(data as IAuditHistoryPayload);
    yield put(DashboardActions.saveAuditHistorySuccess());
    yield put(SidebarActions.toggleLogout(true));
  } catch (error) {
    if (error instanceof Error) {
      yield put(DashboardActions.saveAuditHistoryError(error));
    }
  }
}

export function* getAssessmentStateWorker(action: DashboardAction) {
  try {
    const { data } = action;
    const response: IAssessmentStateResponse = yield DashboardService.getAssessmentsDetails(
      data as string,
    );
    yield put(DashboardActions.getAssessmentDataSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(DashboardActions.getAssessmentDataError(error));
    }
  }
}

export function* hardReloadWorker() {
  yield put(DashboardActions.setActiveDashboardTitleState(DashboardHeaderConstants.cloudSummary));
}

export function* dashboardWatcher() {
  // takeLatest on the bootstrap fetches: if a second mount dispatches the
  // same action while one is in-flight, cancel the older one. Combined
  // with the freshness cache in getAllAccountsWorker this collapses the
  // duplicate dispatches from Dashboard / OverviewHeader / DashboardMobile
  // into a single network call on cold start.
  yield takeLatest(DashboardActionTypes.GET_ALL_ACCOUNTS_START, getAllAccountsWorker);
  yield takeLatest(DashboardActionTypes.GET_AUDIT_HISTORY_START, getAuditHistoryWorker);
  yield takeEvery(DashboardActionTypes.SAVE_AUDIT_HISTORY_START, saveAuditHistoryWorker);
  yield takeLatest(DashboardActionTypes.GET_PROJECT_OWNERS_START, getProjectOwnersWorker);
  yield takeLatest(DashboardActionTypes.GET_ASSESSMENTS_STATE_START, getAssessmentStateWorker);
  yield takeEvery('persist/REHYDRATE', hardReloadWorker);
}
